/* ══════════════════════════════════════
   Monmouth Cards — Search JS
   assets/monmouth-search.js
   ══════════════════════════════════════ */
(function () {
  'use strict';

  /* ── Predictive Search Dropdown ──
     Binds to every search input on the page. The header uses
     .mc-nav__search-input, the mobile drawer uses #mc-mobile-search-input,
     and the search page uses .mc-search-bar__input. Each gets its own
     dropdown element injected right after its form so results are scoped. */
  var debounceTimer  = null;
  var currentQuery   = '';

  /* Real race-condition fix (referral-code-race-condition-add-to-cart-
     prompt.md) — reads the mc_ref_code cookie set by layout/theme.liquid
     and returns it as a cart-attributes fragment to merge into a /cart/
     add.js call, so the code lands in the SAME request that adds the
     item instead of racing a separate background sync call. */
  function mcGetReferralAttribute() {
    var m = document.cookie.match(/(?:^|; )mc_ref_code=([^;]*)/);
    return m ? { mc_ref_code: decodeURIComponent(m[1]) } : {};
  }

  function debounce(fn, ms) {
    return function () {
      var args = arguments;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function () { fn.apply(null, args); }, ms);
    };
  }

  function closeDropdown(dropdown) {
    if (dropdown) {
      dropdown.classList.remove('mc-search-dropdown--open');
      dropdown.innerHTML = '';
    }
  }

  function buildDropdownHTML(data) {
    var html = '';

    /* Collection suggestions */
    var collections = (data.resources && data.resources.results && data.resources.results.collections) || [];
    if (collections.length) {
      html += '<div class="mc-search-dropdown__section">';
      html += '<p class="mc-search-dropdown__label">Collections</p>';
      collections.slice(0, 4).forEach(function (col) {
        html += '<a href="' + col.url + '" class="mc-search-dropdown__col-item">';
        if (col.featured_image && col.featured_image.url) {
          html += '<img src="' + col.featured_image.url + '" alt="' + escapeHtml(col.title) + '" width="36" height="36" loading="lazy">';
        } else {
          html += '<span class="mc-search-dropdown__col-thumb"></span>';
        }
        html += '<span>' + escapeHtml(col.title) + '</span>';
        html += '</a>';
      });
      html += '</div>';
    }

    /* Product suggestions */
    var products = (data.resources && data.resources.results && data.resources.results.products) || [];
    if (products.length) {
      html += '<div class="mc-search-dropdown__section">';
      html += '<p class="mc-search-dropdown__label">Products</p>';
      products.slice(0, 5).forEach(function (prod) {
        var price = prod.price ? formatMoney(prod.price) : '';
        var img   = prod.featured_image && prod.featured_image.url ? prod.featured_image.url : '';
        html += '<a href="' + prod.url + '" class="mc-search-dropdown__prod-item">';
        if (img) {
          html += '<img src="' + img + '" alt="' + escapeHtml(prod.title) + '" width="48" height="48" loading="lazy">';
        } else {
          html += '<span class="mc-search-dropdown__prod-thumb"></span>';
        }
        html += '<span class="mc-search-dropdown__prod-info">';
        html += '<span class="mc-search-dropdown__prod-title">' + escapeHtml(prod.title) + '</span>';
        if (price) html += '<span class="mc-search-dropdown__prod-price">' + price + '</span>';
        html += '</span>';
        html += '</a>';
      });
      html += '</div>';

      /* View all results link */
      html += '<a href="/search?q=' + encodeURIComponent(currentQuery) + '" class="mc-search-dropdown__view-all">View all results for &ldquo;' + escapeHtml(currentQuery) + '&rdquo; &rarr;</a>';
    }

    if (!collections.length && !products.length) {
      html += '<p class="mc-search-dropdown__empty">No results for &ldquo;' + escapeHtml(currentQuery) + '&rdquo;</p>';
    }

    return html;
  }

  function fetchSuggestions(q, dropdown) {
    currentQuery = q;
    if (q.length < 2) { closeDropdown(dropdown); return; }

    try {
      if (window.mcTrack && typeof window.mcTrack.search === 'function') {
        window.mcTrack.search(q);
      }
    } catch (e) { /* no-op */ }

    var url = '/search/suggest.json?q=' + encodeURIComponent(q)
      + '&resources[type]=product,collection'
      + '&resources[limit]=6'
      + '&resources[options][unavailable_products]=last';

    fetch(url, { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (!dropdown) return;
        dropdown.innerHTML = buildDropdownHTML(data);
        dropdown.classList.add('mc-search-dropdown--open');
      })
      .catch(function () { closeDropdown(dropdown); });
  }

  /* Initialize predictive search on every search input present. */
  function initSearchInput(input) {
    if (!input || input.dataset.mcSearchBound) return;
    input.dataset.mcSearchBound = '1';

    var form = input.closest('form');
    /* Find an existing dropdown as a sibling, else create one. */
    var dropdown = (form && form.parentNode && form.parentNode.querySelector('.mc-search-dropdown'))
      || document.createElement('div');
    if (!dropdown.classList.contains('mc-search-dropdown')) {
      dropdown.className = 'mc-search-dropdown';
      if (form) {
        form.parentNode.style.position = form.parentNode.style.position || 'relative';
        form.parentNode.appendChild(dropdown);
      }
    }

    input.addEventListener('input', debounce(function () {
      fetchSuggestions(input.value.trim(), dropdown);
    }, 220));

    input.addEventListener('focus', function () {
      if (input.value.trim().length >= 2) {
        fetchSuggestions(input.value.trim(), dropdown);
      }
    });

    input.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeDropdown(dropdown);
    });

    document.addEventListener('click', function (e) {
      if (form && !form.contains(e.target) && !dropdown.contains(e.target)) {
        closeDropdown(dropdown);
      }
    });
  }

  [
    '.mc-search-bar__input',   // dedicated search page
    '.mc-nav__search-input',   // header
    '#mc-mobile-search-input'  // mobile drawer
  ].forEach(function (sel) {
    document.querySelectorAll(sel).forEach(initSearchInput);
  });

  /* ── Search Filter Chips ── */
  var filterChips = document.querySelectorAll('.mc-search-filter-chip');

  filterChips.forEach(function (chip) {
    chip.addEventListener('click', function () {
      filterChips.forEach(function (c) { c.classList.remove('mc-search-filter-chip--active'); });
      chip.classList.add('mc-search-filter-chip--active');

      var type  = chip.dataset.type || '';
      var q     = getQueryParam('q') || '';
      var url   = new URL(window.location.href);

      if (type) {
        url.searchParams.set('filter.p.product_type', type);
      } else {
        url.searchParams.delete('filter.p.product_type');
      }
      url.searchParams.set('q', q);
      url.searchParams.delete('page');

      window.location.href = url.toString();
    });
  });

  /* ── Sort Dropdown ── */
  var sortSelect = document.querySelector('.mc-search-sort__select');
  if (sortSelect) {
    sortSelect.addEventListener('change', function () {
      var url = new URL(window.location.href);
      url.searchParams.set('sort_by', sortSelect.value);
      url.searchParams.delete('page');
      window.location.href = url.toString();
    });
  }

  /* ── Add to Cart from Search Card ── */
  document.addEventListener('click', function (e) {
    var atcBtn = e.target.closest('.mc-search-card__atc');
    if (!atcBtn) return;

    var variantId = atcBtn.dataset.variantId;
    if (!variantId) return;

    var original = atcBtn.textContent;
    atcBtn.textContent = 'Adding…';
    atcBtn.disabled    = true;

    fetch('/cart/add.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body:    JSON.stringify({ id: variantId, quantity: 1, attributes: mcGetReferralAttribute() })
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data.status) {
          atcBtn.textContent = 'Error';
          atcBtn.disabled    = false;
          setTimeout(function () { atcBtn.textContent = original; }, 2000);
          return;
        }
        atcBtn.textContent = '✓ Added!';
        try { sessionStorage.setItem('mc_last_added_variant_id', String(data.variant_id || variantId)); } catch (e) { /* no-op */ }

        /* Real fix (ga4-analytics-tracking-prompt.md part 4) — this real, working AJAX add-to-cart path never called mcTrack.addToCart at all. Same shape as the PDP version (assets/monmouth-pdp.js). */
        try {
          if (window.mcTrack && typeof window.mcTrack.addToCart === 'function') {
            window.mcTrack.addToCart({
              id: data.product_id || variantId,
              title: data.product_title || data.title,
              price: (typeof data.price === 'number' ? data.price / 100 : undefined)
            }, 1);
          }
        } catch (e) { /* no-op */ }

        document.dispatchEvent(new CustomEvent('mc:cart-updated'));
        document.dispatchEvent(new CustomEvent('mc:open-cart-drawer'));
        setTimeout(function () {
          atcBtn.textContent = original;
          atcBtn.disabled    = false;
        }, 1600);
      })
      .catch(function () {
        atcBtn.textContent = 'Error';
        atcBtn.disabled    = false;
        setTimeout(function () { atcBtn.textContent = original; }, 2000);
      });
  });

  /* ── Utilities ── */
  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function formatMoney(amount) {
    return '$' + parseFloat(amount).toFixed(2).replace(/\.00$/, '');
  }

  function getQueryParam(key) {
    return new URLSearchParams(window.location.search).get(key) || '';
  }

})();
